import { CF_URLS } from '../../../config/api';

const PROXY_URL = CF_URLS.claudeProxy;

// Response content block (from Anthropic API response)
interface ClaudeResponseBlock {
  type: 'text';
  text: string;
}

interface ClaudeResponse {
  content: ClaudeResponseBlock[];
  model: string;
  stop_reason: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

// Request content block (for sending to Anthropic API)
interface ClaudeRequestBlock {
  type: 'text' | 'image' | 'document';
  text?: string;
  source?: {
    type: 'base64';
    media_type: string;
    data: string;
  };
}

interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string | ClaudeRequestBlock[];
}

/** Fetch with an AbortController timeout */
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Retry helper for transient Cloud Function failures (cold starts, fetch failed) */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 1,
  timeoutMs = 510000,
): Promise<Response> {
  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeoutMs);
      if (response.ok || response.status < 500) return response;
      if (attempt <= maxRetries) {
        const errorText = await response.text();
        console.warn(`[Claude] Attempt ${attempt} got ${response.status}, retrying in 3s... (${errorText.slice(0, 100)})`);
        await new Promise(r => setTimeout(r, 3000));
        continue;
      }
      return response;
    } catch (err) {
      if (attempt <= maxRetries) {
        console.warn(`[Claude] Attempt ${attempt} failed, retrying in 3s...`, err);
        await new Promise(r => setTimeout(r, 3000));
        continue;
      }
      throw err;
    }
  }
  throw new Error('fetchWithRetry: unreachable');
}

/**
 * Call Claude via the Firebase Cloud Function proxy.
 * Returns the text content from the first response block.
 * Includes automatic retry for transient Cloud Function failures.
 */
export async function claudeGenerate(
  model: string,
  systemPrompt: string,
  userMessage: string,
  temperature = 0,
  maxTokens = 4096,
): Promise<string> {
  const messages: ClaudeMessage[] = [
    { role: 'user', content: userMessage },
  ];

  const response = await fetchWithRetry(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      system: systemPrompt,
      messages,
      max_tokens: maxTokens,
      temperature,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error (${response.status}): ${errorText}`);
  }

  const data: ClaudeResponse = await response.json();
  return data.content
    .filter((block) => block.type === 'text')
    .map((block) => block.text)
    .join('');
}

export interface ClaudeCallMetadata {
  sessionId?: string;
  callerFunction?: string;
}

/**
 * Call Claude with multi-turn conversation history.
 */
export async function claudeChat(
  model: string,
  systemPrompt: string,
  history: ClaudeMessage[],
  userMessage: string,
  temperature = 0,
  maxTokens = 4096,
  metadata?: ClaudeCallMetadata,
): Promise<string> {
  const messages: ClaudeMessage[] = [
    ...history,
    { role: 'user', content: userMessage },
  ];

  const response = await fetchWithTimeout(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      system: systemPrompt,
      messages,
      max_tokens: maxTokens,
      temperature,
      ...(metadata?.callerFunction && { callerFunction: metadata.callerFunction }),
      ...(metadata?.sessionId && { sessionId: metadata.sessionId }),
    }),
  }, 120000);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error (${response.status}): ${errorText}`);
  }

  const data: ClaudeResponse = await response.json();
  return data.content
    .filter((block) => block.type === 'text')
    .map((block) => block.text)
    .join('');
}

/**
 * Call Claude with vision (image + text) for Lab Analyst.
 */
export async function claudeVision(
  model: string,
  systemPrompt: string,
  imageBase64: string,
  mimeType: string,
  userMessage: string,
  temperature = 0,
  maxTokens = 4096,
): Promise<string> {
  // Use 'document' type for PDFs, 'image' for everything else
  const isPDF = mimeType === 'application/pdf';
  const contentType: 'document' | 'image' = isPDF ? 'document' : 'image';

  const messages: ClaudeMessage[] = [
    {
      role: 'user',
      content: [
        {
          type: contentType,
          source: {
            type: 'base64',
            media_type: mimeType,
            data: imageBase64,
          },
        },
        {
          type: 'text',
          text: userMessage,
        },
      ],
    },
  ];

  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      system: systemPrompt,
      messages,
      max_tokens: maxTokens,
      temperature,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error (${response.status}): ${errorText}`);
  }

  const data: ClaudeResponse = await response.json();
  return data.content
    .filter((block) => block.type === 'text')
    .map((block) => block.text)
    .join('');
}
